import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import AssessmentViewerPicker from "@components/Organization/AssessmentViewerPicker";
import AssessmentViewerLinksPanel from "@components/Organization/AssessmentViewerLinksPanel";
import { PERMISSIONS } from "@util/permissions";
import usePermission from "@util/usePermission";
import { notifyUserFacingError } from "@util/toErrorMessage";
import { fetchUserOrganizations } from "./userOrganizationsApi";
import { orgInstrumentSelectOptions } from "@components/Organization/orgInstrumentSelectOptions";

export default function UserAssessmentAssign({
  userId,
  firstName = "",
  onAssigned = () => true,
}) {
  const { t } = useTranslation();
  const canAssign = usePermission(PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT);

  const [managing, setManaging] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [instrumentId, setInstrumentId] = useState(null);
  const [instrumentOptions, setInstrumentOptions] = useState([]);
  const [viewerKeys, setViewerKeys] = useState([]);
  const [viewerIds, setViewerIds] = useState({
    organization_user_admin_ids: [],
    organization_user_manager_ids: [],
  });
  const [sendEmail, setSendEmail] = useState(true);
  const [loadingInstruments, setLoadingInstruments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignedAssessmentId, setAssignedAssessmentId] = useState(null);
  const [assignedInstrumentLabel, setAssignedInstrumentLabel] = useState(null);

  const orgOptions = (organizations || []).map((organization) => ({
    value: String(organization.id),
    label: organization.name || String(organization.id),
  }));

  useEffect(() => {
    if (!managing || !userId || !canAssign) return;

    setLoadingOrgs(true);
    setOrganizations([]);
    fetchUserOrganizations(userId)
      .then((rows) => {
        setOrganizations(rows);
        setLoadingOrgs(false);
      })
      .catch((err) => {
        setLoadingOrgs(false);
        setOrganizations([]);
        notifyUserFacingError(err, toast, {
          source: "user_assessment_assign_organizations",
        });
      });
  }, [managing, userId, canAssign]);

  useEffect(() => {
    setInstrumentId(null);
    setInstrumentOptions([]);
    resetViewers();
    setAssignedAssessmentId(null);

    if (!organizationId || !canAssign || !managing) return;

    setLoadingInstruments(true);
    Promise.all([
      axios.post(
        `/organizations/${organizationId}/retrieve-assessment-instruments/`,
        { page: 0, page_size: 200 }
      ),
      axios.post(`/retrieve-assessment-instruments/`, {
        scope: "active",
        page: 0,
        page_size: 200,
      }),
    ])
      .then(([instrumentsRes, catalogRes]) => {
        const joined = instrumentsRes.data.response[0]?.data || [];
        const catalog = catalogRes.data.response[0]?.data || [];
        setInstrumentOptions(orgInstrumentSelectOptions(joined, catalog, t));
        setLoadingInstruments(false);
      })
      .catch((err) => {
        setLoadingInstruments(false);
        notifyUserFacingError(err, toast, {
          source: "user_assessment_assign_instruments",
        });
      });
  }, [organizationId, canAssign, managing]);

  function resetViewers() {
    setViewerKeys([]);
    setViewerIds({
      organization_user_admin_ids: [],
      organization_user_manager_ids: [],
    });
  }

  function resetForm() {
    setOrganizationId(null);
    setInstrumentId(null);
    setInstrumentOptions([]);
    setOrganizations([]);
    resetViewers();
    setSendEmail(true);
    setAssignedAssessmentId(null);
    setAssignedInstrumentLabel(null);
  }

  function submit() {
    if (!userId || !organizationId || !instrumentId) return;
    setSubmitting(true);

    const body = {
      user_id: userId,
      organization_id: organizationId,
      send_email: sendEmail,
    };
    if (viewerIds.organization_user_admin_ids.length) {
      body.organization_user_admin_ids = viewerIds.organization_user_admin_ids;
    }
    if (viewerIds.organization_user_manager_ids.length) {
      body.organization_user_manager_ids =
        viewerIds.organization_user_manager_ids;
    }

    const instrumentLabel =
      instrumentOptions.find((option) => option.value === String(instrumentId))
        ?.label || String(instrumentId);

    axios
      .post(`/assessment-instruments/${instrumentId}/assign/`, body)
      .then(({ data }) => {
        const payload = data.response[0] || {};
        const assessment = payload.assessment || {};
        setSubmitting(false);
        setAssignedAssessmentId(assessment.id || null);
        setAssignedInstrumentLabel(instrumentLabel);
        setInstrumentId(null);
        resetViewers();
        toast.success(t("updated"));
        onAssigned(assessment);
      })
      .catch((err) => {
        setSubmitting(false);
        notifyUserFacingError(err, toast, { source: "user_assessment_assign" });
      });
  }

  if (!canAssign) {
    return null;
  }

  if (!managing) {
    return (
      <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT}>
        <Group mb="lg">
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => setManaging(true)}
          >
            {t("assign_assessment_to_user", {
              name: firstName || t("user_singular"),
            })}
          </Button>
        </Group>
      </Can>
    );
  }

  if (loadingOrgs) {
    return (
      <Group mb="lg" justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!orgOptions.length) {
    return (
      <Stack gap="sm" mb="lg">
        <EmptyMessage
          title={t("cs:organization_plural")}
          text={t("user_needs_organization_for_assign")}
        />
        <Group>
          <Button
            variant="default"
            onClick={() => {
              resetForm();
              setManaging(false);
            }}
          >
            {t("cancel")}
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT}>
      <Stack gap="md" mb="lg">
        <Card>
          <Stack gap="sm">
            <Title order={5}>
              {t("assign_assessment_to_user", {
                name: firstName || t("user_singular"),
              })}
            </Title>
            <Select
              label={t("cs:organization_singular")}
              searchable
              clearable
              data={orgOptions}
              value={organizationId}
              onChange={(value) => setOrganizationId(value)}
            />
            <Select
              label={t("assessment_instrument_singular")}
              searchable
              clearable
              data={instrumentOptions}
              value={instrumentId}
              onChange={setInstrumentId}
              disabled={!organizationId || loadingInstruments}
            />
            {organizationId && (
              <AssessmentViewerPicker
                organizationId={organizationId}
                userId={userId}
                mode="multi"
                value={viewerKeys}
                onChange={({
                  keys,
                  organization_user_admin_ids,
                  organization_user_manager_ids,
                }) => {
                  setViewerKeys(keys || []);
                  setViewerIds({
                    organization_user_admin_ids:
                      organization_user_admin_ids || [],
                    organization_user_manager_ids:
                      organization_user_manager_ids || [],
                  });
                }}
              />
            )}
            <Checkbox
              label={t("send_email")}
              checked={sendEmail}
              onChange={(event) => setSendEmail(event.currentTarget.checked)}
            />
            <Group>
              <Button
                disabled={!organizationId || !instrumentId}
                loading={submitting}
                onClick={submit}
              >
                {t("assign")}
              </Button>
              <Button
                variant="default"
                disabled={submitting}
                onClick={() => {
                  resetForm();
                  setManaging(false);
                }}
              >
                {t("cancel")}
              </Button>
            </Group>
          </Stack>
        </Card>

        {assignedAssessmentId && (
          <Card>
            <Stack gap="xs" mb="sm">
              <Text fw={600}>{t("assessment_assigned")}</Text>
              <Text size="sm">
                {t("assessment_instrument_singular")}:{" "}
                {assignedInstrumentLabel || "—"}
              </Text>
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  {t("assessment_singular")}: {assignedAssessmentId}
                </Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(String(assignedAssessmentId))
                      .then(() => toast.success(t("copied")))
                      .catch(() => toast.error(t("copy_failed")));
                  }}
                >
                  {t("copy")}
                </Button>
              </Group>
            </Stack>
            <Text size="sm" mb="xs">
              {t("assessment_viewer_plural")}
            </Text>
            <AssessmentViewerLinksPanel
              organizationId={organizationId}
              assessmentId={assignedAssessmentId}
              assigneeUserId={userId}
              allowMutate
            />
          </Card>
        )}
      </Stack>
    </Can>
  );
}
